import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  ClipboardCheck,
  HandHelping,
  ShieldCheck,
  Stethoscope,
  Users
} from 'lucide-react';
import { AUSTRALIA_CARE_WORKFORCE_INTAKE_FORM } from '../constants';
import { AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN } from './AustraliaCareWorkforceIntakePage';

const programSteps = [
  {
    title: 'Send In Your Intake Form',
    description:
      'Tell us accurately where you stand on readiness, how you communicate, what experience you hold, and the background behind it.'
  },
  {
    title: 'First-Stage Review',
    description:
      'Every submission is looked at for early suitability. Where someone is shortlisted, we may make contact to check communication, review documents, and continue screening.'
  },
  {
    title: 'Preparation and Training',
    description:
      'Those who carry on may take part in structured preparation that works through readiness, communication, what workplaces expect, and the nature of care-support roles.'
  },
  {
    title: 'Evaluation',
    description:
      'Evaluations at this point may cover role-readiness, behavioral, communication, or practical ability.'
  },
  {
    title: 'Employer Consideration and Matching',
    description:
      'Where openings exist in Australia, a candidate who reaches the required standard may have a profile put forward to suitable employers.'
  },
  {
    title: 'Guidance For Later Stages',
    description:
      'Anyone who moves ahead may be given guidance covering later-stage requirements, compliance, documentation, and interviews.'
  }
];

const programIncludes = [
  'an organised candidate intake process',
  'suitability review and screening',
  'checks on readiness and communication',
  'training support and preparation',
  'orientation to the role',
  'evaluation and assessment',
  'help preparing for interviews, where that applies',
  'putting candidate profiles forward for employer consideration',
  'next-step guidance for those who move ahead'
];

const suitabilityPoints = [
  'are drawn to work in a care setting',
  'bring experience from disability support, elderly care, hospital support, home care, community care, or comparable service roles',
  'feel at ease taking on personal care duties when those are needed',
  'hold English communication at a working professional standard',
  'welcome structured training alongside assessment',
  'accept that this is a formal route and will follow it',
  'would consider relocating, if selected and if every requirement is met'
];

const ethicalPrinciples = [
  {
    icon: ShieldCheck,
    title: 'Transparency',
    description:
      'The program is set out plainly, so that what is on offer stays separate from what still depends on review.'
  },
  {
    icon: HandHelping,
    title: 'Fairness In Practice',
    description:
      'Anyone taking part should meet clear expectations, professional screening, and respectful contact.'
  },
  {
    icon: Users,
    title: 'Protecting Workers',
    description:
      'How the process is built aims to support informed participation and cut down on misleading expectations.'
  },
  {
    icon: BadgeCheck,
    title: 'A Responsibly Built Process',
    description:
      'Nothing advances by default; documentation, legal, and employer requirements continue to apply.'
  }
];

const commitments = [
  {
    title: 'Honest, Plain Communication',
    description:
      'Information about the program is given plainly, and intake is never described as a guaranteed migration outcome or job.'
  },
  {
    title: 'Nothing Overstated',
    description:
      'Deceptive or exaggerated claims have no place here, and that includes any promise of guaranteed relocation, visas, or employment.'
  },
  {
    title: 'Open About Each Stage',
    description:
      'The major stages are spelled out, so that candidates can tell what is on offer apart from what remains under review.'
  },
  {
    title: 'Dignity Of Every Candidate',
    description:
      'Fairness, professionalism, and respect should mark the way people are treated at every point in the process.'
  },
  {
    title: 'Decisions Made With Full Information',
    description:
      'Nobody should take a further step without holding enough information to weigh that decision properly.'
  },
  {
    title: 'Expectations Kept Realistic',
    description:
      'A clear line is drawn between any actual job offer and the stages that precede it: intake, preparation, assessment, and employer consideration.'
  },
  {
    title: 'Guarding Against Exploitation',
    description:
      'Lowering the risk of exploitative recruitment behavior, unfair treatment, coercion, or misinformation is treated as a priority.'
  },
  {
    title: 'Legal And Employer Requirements Respected',
    description:
      'Destination-country, regulatory, and employer requirements continue to govern any step into employment-related stages.'
  }
];

const safeguards = [
  'a plain account of why the program exists',
  'an unambiguous statement that intake is not employment',
  'the stages set out before anyone progresses',
  'key conditions shared ahead of any major decision',
  'professional conduct throughout screening and assessment',
  'the chance to see what is being evaluated',
  'a firm line between preparation stages and anything employer-led that follows'
];

const eligibilitySnapshot = [
  'supporting documentation, together with valid identification',
  'accuracy and truthfulness in completing the intake process',
  'a readiness to take part in screening and assessment',
  'the ability to communicate in English',
  'preparedness for structured training or preparation',
  'professional conduct, and following the instructions given for the program',
  'a willingness to relocate on selection',
  'the capacity to meet administrative and employer requirements at later stages'
];

const faqs = [
  {
    question: 'Is this a job offer?',
    answer:
      'No. What it covers is candidate intake, screening, and the possibility of moving through a structured program. Treat it as neither a job offer nor a promise of relocation, sponsorship, or employment.'
  },
  {
    question: 'Will submitting the form secure me a placement?',
    answer:
      'No. Sending in the intake form means only that your details enter the review process. Nothing about employment, relocation, sponsorship, placement, or progression is guaranteed by it. Whether you advance rests on employer requirements, the opportunities available, your suitability, how you perform at assessment, and the outcome of screening.'
  },
  {
    question: 'What does it cost to take part?',
    answer:
      'For candidates selected at the current intake stage, participation is intended to be fully sponsored. Should a later stage bring third-party costs, whether tied to destination-country, regulatory, travel, medical, or documentation requirements, those should be set out clearly before anyone decides whether to continue.'
  },
  {
    question: 'Which roles does this cover?',
    answer:
      'Attention at present sits with Health Care Assistant and related care-support pathways connected to Australia. Employer needs may widen that across care environments such as disability support, aged care, hospital support, home care, or similar service settings.'
  },
  {
    question: 'What follows my submission?',
    answer:
      'Your intake goes forward for a review of initial suitability. Anyone shortlisted may hear from us about the next stage of the process, about document review, communication checks, or further screening. Moving past that first review will not happen for every applicant.'
  },
  {
    question: 'Is prior experience necessary?',
    answer:
      'It helps, particularly experience gained in hospital support, elderly care, disability support, community care, home care, or related service roles. Even so, advancement turns on the needs of the pathway or its participating employers, on assessment outcomes, on readiness, on communication ability, and on overall suitability.'
  },
  {
    question: 'Is migration to Australia guaranteed?',
    answer:
      'No. Preparation and intake are all this program covers, and migration to Australia is not guaranteed by it. Anything at a later stage stays subject to whatever destination-country rules apply, to administrative and legal processes, to documentation requirements, and to employer decisions.'
  },
  {
    question: 'Why the emphasis on ethical recruitment?',
    answer:
      'Because recruiting across borders ought to be fair, transparent, and protective of workers. Candidates deserve honest communication and a clear picture of the process, free of unrealistic expectations or misleading claims about migration outcomes, sponsorship, or jobs.'
  }
];

const sectionFade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.55 }
};

const openHref = (href) => {
  if (!href) {
    return;
  }

  if (/^https?:\/\//.test(href)) {
    window.open(href, '_blank', 'noopener,noreferrer');
    return;
  }

  window.location.href = href;
};

const SectionHeading = ({ eyebrow, title, description, align = 'left', theme = 'light' }) => (
  <div className={align === 'center' ? 'text-center max-w-3xl mx-auto mb-12' : 'max-w-3xl mb-10'}>
    {eyebrow ? (
      <span
        className={`inline-flex items-center rounded-full text-xs font-medium px-4 py-2 mb-4 ${
          theme === 'dark'
            ? 'bg-white/10 text-white border border-white/15'
            : 'bg-surface-accent text-secondary'
        }`}
      >
        {eyebrow}
      </span>
    ) : null}
    <h2
      className={`text-3xl md:text-4xl font-semibold mb-4 ${
        theme === 'dark' ? 'text-white' : 'text-primary'
      }`}
    >
      {title}
    </h2>
    {description ? (
      <p
        className={`text-base md:text-lg font-light leading-relaxed ${
          theme === 'dark' ? 'text-white/85' : 'text-gray-600'
        }`}
      >
        {description}
      </p>
    ) : null}
  </div>
);

const Checklist = ({ items, columns = 1 }) => (
  <div className={`grid gap-4 ${columns === 2 ? 'md:grid-cols-2' : ''}`}>
    {items.map((item) => (
      <div
        key={item}
        className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <CheckCircle2 size={20} className="text-secondary mt-0.5 flex-shrink-0" />
        <p className="text-sm md:text-base text-gray-700 font-light leading-relaxed">{item}</p>
      </div>
    ))}
  </div>
);

const AustraliaCareWorkforcePage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const applicationsOpen = AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN;

  const handleIntakeClick = () => {
    if (!applicationsOpen) {
      return;
    }

    openHref(AUSTRALIA_CARE_WORKFORCE_INTAKE_FORM);
  };

  const handleLearnHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden bg-gradient-to-br from-surface via-white to-surface-accent">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 h-64 w-64 rounded-full bg-secondary/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-secondary-soft bg-white text-secondary text-xs font-medium px-4 py-2 mb-6">
                <Stethoscope size={14} />
                Australia Care Workforce Candidate Program
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-primary leading-tight mb-6">
                Australia Health Care Assistant Candidate Program
              </h1>
              <p className="text-gray-600 text-base md:text-lg font-light leading-relaxed max-w-3xl mb-8">
                An organised route through screening, preparation, and assessment that can, for
                candidates who qualify, lead on to a connection with suitable care workforce
                openings in Australia.
              </p>

              {applicationsOpen ? null : (
                <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 mb-8">
                  <CalendarClock size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-primary">
                      Intake is closed at present
                    </p>
                    <p className="text-sm text-gray-600 font-light leading-relaxed">
                      Capacity for this cohort has been filled. When the next cohort opens, intake
                      reopens with it — do check back for that announcement.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                {applicationsOpen ? (
                  <button
                    onClick={handleIntakeClick}
                    className="bg-primary hover:bg-primary-hover text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center justify-center gap-2"
                  >
                    Begin Candidate Intake
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="bg-gray-200 text-gray-500 px-8 py-4 rounded-xl text-sm md:text-base inline-flex items-center justify-center gap-2 cursor-not-allowed"
                  >
                    Cohort Full — Intake Closed
                  </button>
                )}
                <button
                  onClick={handleLearnHowItWorks}
                  className="border border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center justify-center gap-2"
                >
                  See How It Works
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-white border border-gray-200 rounded-[28px] p-6 md:p-8 shadow-xl shadow-primary/5"
            >
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {[
                  'Organised intake and review',
                  'Readiness training and preparation',
                  'Progression and assessment checks',
                  'Employer matching where possible'
                ].map((item) => (
                  <div key={item} className="rounded-2xl bg-surface p-4 border border-gray-100">
                    <CheckCircle2 size={18} className="text-secondary mb-3" />
                    <p className="text-sm text-gray-700 font-light leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-primary p-5 text-white">
                <p className="text-xs uppercase tracking-[0.18em] text-white/70 mb-2">
                  Please Note
                </p>
                <p className="text-sm md:text-base font-light leading-relaxed">
                  Sending an intake form is not a job offer, and it guarantees no employment,
                  migration, sponsorship, or placement.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Start Here"
            title="An organised route through intake and preparation"
            description="This program exists for people who wish to be considered on an Australia-focused care workforce route. Its aim is to find candidates who are suitable, prepare them, and then link those who qualify with relevant opportunities where that fits."
            align="center"
          />

        </motion.div>
      </section>

      <section className="py-16 bg-surface-subtle">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="What The Program Covers"
            title="Finding and preparing candidates for care-support routes"
            description="The focus here falls on identifying candidates, then preparing them, for Health Care Assistant and related care-support opportunities in Australia."
          />
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
            <div className="rounded-3xl bg-white border border-gray-200 p-6 md:p-8">
              <p className="text-gray-700 text-base font-light leading-relaxed mb-6">
                What the process involves varies with employer needs and the pathway, but it may
                cover:
              </p>
              <Checklist items={programIncludes.slice(0, 7)} />
            </div>
            <div className="rounded-3xl bg-primary text-white p-6 md:p-8">
              <h3 className="text-2xl font-semibold mb-4">What you will not find here</h3>
              <p className="text-white/90 font-light leading-relaxed">
                There is no general listing of overseas jobs on this page. What it offers instead is
                intake and preparation for candidates willing to be considered by way of a formal
                process.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      <section id="how-it-works" className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Stage By Stage"
            title="The route from intake through to possible progression"
            description="A step-by-step look at the way the program may run."
          />
          <div className="grid lg:grid-cols-2 gap-6">
            {programSteps.map((step, index) => (
              <div
                key={step.title}
                className="rounded-3xl border border-gray-200 bg-surface p-6 md:p-7"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary text-white flex items-center justify-center text-lg font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-primary mb-3">{step.title}</h3>
                <p className="text-gray-600 font-light leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-surface-subtle">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <SectionHeading
              eyebrow="Who Might Suit This Program"
              title="People ready for formal screening, training, and a move abroad"
              description="The route may suit people who:"
            />
            <Checklist items={suitabilityPoints} />
            <p className="text-gray-600 text-sm md:text-base font-light leading-relaxed mt-6">
              Experience of the right kind can help, though what governs progression is employer
              requirements, assessment results, screening outcomes, and overall suitability.
            </p>
          </div>

          <div>
            <SectionHeading
              eyebrow="Components You May Encounter"
              title="Elements that build readiness and help measure it"
              description="These components may form part of the program:"
            />
            <Checklist items={programIncludes} />
            <div className="mt-6 rounded-3xl border border-blue-200 bg-blue-50 p-6">
              <p className="text-gray-700 font-light leading-relaxed">
                Nothing advances by default. Whether a candidate moves ahead rests on
                documentation, employer requirements, the opportunities open at the time,
                performance, and any other conditions that apply.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* <section className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Sponsored Participation"
            title="Participation at the current intake is intended to be fully sponsored for those selected"
            description="Selected candidates face no charge for taking part at the current intake stage wherever sponsorship applies."
          />
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8">
            <div className="rounded-3xl border border-gray-200 bg-surface p-6 md:p-8">
              <p className="text-gray-700 font-light leading-relaxed mb-4">
                Where a later stage brings destination-country, visa, medical, travel, regulatory,
                or third-party administrative requirements, that should be made clear before anyone
                decides to proceed.
              </p>
              <p className="text-gray-700 font-light leading-relaxed">
                Everyone taking part should know what is covered now and what could arise later on.
              </p>
            </div>
            <div className="rounded-3xl bg-primary text-white p-6 md:p-8">
              <h3 className="text-xl font-semibold mb-3">Transparency first</h3>
              <p className="text-white/85 font-light leading-relaxed">
                Talking openly about expectations, stages, and costs forms part of how the program
                is run.
              </p>
            </div>
          </div>
        </motion.div>
      </section> */}

      <section className="py-16 bg-surface-subtle">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Ethical Recruitment"
            title="Worker protection and ethical recruitment sit at the centre of this"
            description="Running the program fairly, transparently, and with respect for the people in it is a commitment we hold to."
          />
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
            {ethicalPrinciples.map((principle) => {
              const Icon = principle.icon;

              return (
                <div
                  key={principle.title}
                  className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-4">
                    <Icon size={22} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-3">{principle.title}</h3>
                  <p className="text-gray-600 font-light leading-relaxed text-sm">
                    {principle.description}
                  </p>
                </div>
              );
            })}
          </div>
          {/* <div className="rounded-3xl border border-blue-200 bg-blue-50 p-6 md:p-8">
            <p className="text-gray-700 font-light leading-relaxed">
              Recognized standards on worker protection and ethical recruitment inform the way we
              work.
            </p>
          </div> */}
        </motion.div>
      </section>

      <section className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="The Commitments We Make"
            title="Protecting candidate dignity and keeping expectations honest"
            description="They exist to keep the process professional, fair, and clear."
          />
          <div className="grid md:grid-cols-2 gap-6">
            {commitments.map((commitment, index) => (
              <div key={commitment.title} className="rounded-3xl bg-surface border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-secondary font-semibold">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-primary">{commitment.title}</h3>
                </div>
                <p className="text-gray-600 font-light leading-relaxed">{commitment.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-surface-subtle">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10">
          <div>
          <SectionHeading
            eyebrow="Candidate Safeguards"
            title="What candidates are entitled to expect"
            description="These safeguards apply while the process runs:"
          />
            <Checklist items={safeguards} />
          </div>

          <div>
            <SectionHeading
              eyebrow="Important Notice"
              title="Intake is not employment"
              description="No job offer arises from an intake submission, and no employment, migration, sponsorship, or placement is guaranteed by it."
            />
            <div className="rounded-3xl bg-primary text-white p-6 md:p-8">
              <div className="flex items-start gap-3 mb-5">
                <CircleAlert size={22} className="mt-0.5 flex-shrink-0" />
                <p className="font-light leading-relaxed">
                  What determines progression is the opportunities available, legal processes,
                  employer requirements, documentation, candidate suitability, assessment results,
                  and screening outcomes.
                </p>
              </div>
              <p className="text-white/80 text-sm font-light leading-relaxed">
                Rely only on formal written communication issued through the official process.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeading
              eyebrow="Eligibility Snapshot"
              title="Broad readiness signals used at early review"
              description="Candidates may need to show:"
            />
            <Checklist items={eligibilitySnapshot} />
            <p className="text-gray-600 text-sm md:text-base font-light leading-relaxed mt-6">
              These form an opening snapshot only; meeting them guarantees nothing about
              progression.
            </p>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-surface p-6 md:p-8">
            <span className="inline-flex items-center rounded-full bg-white text-secondary text-xs font-medium px-4 py-2 mb-4">
              Why Ethics Matters Here
            </span>
            <h3 className="text-2xl font-semibold text-primary mb-4">
              Respect for rights belongs at the outset of cross-border recruitment
            </h3>
            <p className="text-gray-700 font-light leading-relaxed mb-4">
              Since this route concerns international workforce opportunities, ethical thinking has
              to be designed in rather than added later.
            </p>
            <p className="text-gray-700 font-light leading-relaxed">
              For that reason employment is never promised on this page; the emphasis falls instead
              on a process that is structured and clear.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-surface-subtle">
        <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Frequently Asked Questions"
            title="Questions candidates ask most often"
            description="Brief answers to the points that come up regularly."
            align="center"
          />
          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;

              return (
                <div
                  key={faq.question}
                  className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-base md:text-lg font-semibold text-primary">
                      {faq.question}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 border-t border-gray-100 pt-4 text-gray-600 font-light leading-relaxed">
                          {faq.answer}
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-gradient-to-br from-primary to-secondary">
        <motion.div {...sectionFade} className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <SectionHeading
            eyebrow={applicationsOpen ? 'Next Step' : 'Next Cohort'}
            title={applicationsOpen ? 'Would You Like to Be Considered?' : 'Capacity Reached for This Cohort'}
            description={
              applicationsOpen
                ? 'Complete the official intake form to put yourself forward for this Australia-focused care workforce route.'
                : 'The present cohort is no longer taking applications. Intake reopens with the next cohort, so please check back for that announcement.'
            }
            align="center"
            theme="dark"
          />
          <div className="flex justify-center">
            {applicationsOpen ? (
              <button
                onClick={handleIntakeClick}
                className="bg-white hover:bg-gray-100 text-primary px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center gap-2"
              >
                Open the Intake Form
                <ArrowRight size={18} />
              </button>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 text-white px-8 py-4 text-sm md:text-base">
                <CalendarClock size={18} />
                Intake Closed
              </span>
            )}
          </div>
        </motion.div>
      </section>

      <section id="intake-form" className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="rounded-[32px] border border-dashed border-secondary-soft bg-surface p-8 md:p-10 text-center">
            {applicationsOpen ? (
              <>
                <ClipboardCheck size={28} className="mx-auto text-secondary mb-4" />
                <h2 className="text-3xl font-semibold text-primary mb-4">
                  Begin your screening application
                </h2>
                <p className="text-gray-600 font-light leading-relaxed max-w-2xl mx-auto mb-8">
                  Share your background, and your interest in the program, through the official
                  intake route.
                </p>
                <button
                  onClick={handleIntakeClick}
                  className="bg-primary hover:bg-primary-hover text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center gap-2"
                >
                  Send Intake Form
                  <ArrowRight size={18} />
                </button>
              </>
            ) : (
              <>
                <CalendarClock size={28} className="mx-auto text-secondary mb-4" />
                <h2 className="text-3xl font-semibold text-primary mb-4">
                  New applications are paused
                </h2>
                <p className="text-gray-600 font-light leading-relaxed max-w-2xl mx-auto">
                  With this cohort now full, intake submissions have been put on hold. They will
                  resume once the next cohort opens — do check back for that announcement.
                </p>
              </>
            )}
            <p className="text-xs text-gray-500 font-light leading-relaxed mt-6 max-w-3xl mx-auto">
              Program information and candidate intake are the only purposes of this page. Nothing
              on it guarantees placement, a migration outcome, sponsorship, or employment.
            </p>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AustraliaCareWorkforcePage;
